/**
 * useAddContentSubmitSeams Hook
 *
 * Resolves the Preview-"Done" submit handler for the AddContent sheet's four
 * "message media" flows (chat / community / feedback / profileImage) from the
 * single `flowType` discriminator. Extracted from `AddContentSheetContent` to
 * keep that component under the size ceiling (`presentation.md` → "📐 Component
 * size"), mirroring the earlier `resolveDropStepConfig` extraction. Pure
 * relocation — every seam is behaviour-identical to the inline block it
 * replaced.
 *
 * Each seam parameterizes the shared, UI-agnostic `useSendMessageMedia`
 * collector; the split between them is exactly the send-vs-collect axis:
 *
 * - **chat / community** are SEND seams: "Done" gates on a Confirm sheet, then
 *   hands the collected media to the owning feature hook (which owns the
 *   optimistic tile + upload/persist) and toasts on upload failure.
 * - **feedback / profileImage** are COLLECT-NOT-SEND seams: "Done" hands the
 *   picked `{ file, aspectRatio }` straight back to the caller with NO upload,
 *   NO confirm sheet and NO failure toast. Their adapters always resolve `true`,
 *   so there is no failure path to report — hence no `onError` (see each seam).
 *
 * Layer note — please do not "fix" the sheet/toast back into the component.
 * The "a hook never fires a sheet/toast" rule (`presentation.md` §"Page +
 * feature-hook split") is scoped to a PAGE feature hook
 * (`feature/[feature]/hooks/use[Feature].ts`); every escape that taught it
 * cites one (`useCommunity`, `useVideoCall`, `useSendGift`). This is a
 * component-scoped sheet-content hook, which the same section carves out.
 *
 * The invariant that DOES bind holds here: the reusable collector stays
 * surface-agnostic — `useSendMessageMedia` never imports a sheet or a toast,
 * it only awaits the injected `onConfirm` boolean and calls the injected
 * `onError`. The surface choice is isolated to this one seam-wiring hook,
 * matching the folder's pre-existing convention (`usePublishContent.ts:96-111`
 * fires the same `ConfirmSheet` + `toast.error` pair).
 */

import { useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { ConfirmSheet } from '@/presentation/components/common/sheet/confirm/ConfirmSheet';
import { appLogger } from '@/domain/services/analytics/appLogger';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AddContentFlowType } from '@/domain/enums/publish/addContentFlowType';
import type { MediaType } from '@/domain/enums/mediaType';
import type { VideoMeta } from '@/domain/entities/publish/videoMeta';
import type {
  ChatAddContentOptions,
  CommunityAddContentOptions,
  FeedbackAddContentOptions,
  ProfileImageAddContentOptions,
} from '../AddContentSheet';
import { useSendMessageMedia } from './useSendMessageMedia';

interface UseAddContentSubmitSeamsParams {
  /** The resolved flow discriminator — selects which seam `submit` returns. */
  flowType: AddContentFlowType;
  selectedFile: File | null;
  mediaType: MediaType | null;
  videoMeta: VideoMeta | null;
  onFinish: () => void;
  /** Screen key for `ConfirmSheet` association (send seams only). */
  screenKey?: string;
  chat?: ChatAddContentOptions;
  community?: CommunityAddContentOptions;
  feedback?: FeedbackAddContentOptions;
  profileImage?: ProfileImageAddContentOptions;
}

export const useAddContentSubmitSeams = ({
  flowType,
  selectedFile,
  mediaType,
  videoMeta,
  onFinish,
  screenKey,
  chat,
  community,
  feedback,
  profileImage,
}: UseAddContentSubmitSeamsParams) => {
  const { l10n } = useLocale();

  // Confirm sheet reused by both SEND flows (chat + community). The hook owns
  // the sheet so the collector (`useSendMessageMedia`) stays UI-agnostic — it
  // only awaits the confirmed boolean. Modeled on usePublishContent.ts:96-111;
  // a deliberate divergence from Flutter's straight-to-send "Done" (round-3 user
  // review).
  const showSendMediaConfirm = useCallback(async (): Promise<boolean> => {
    try {
      const response = await ConfirmSheet.show({
        title: l10n.publish.confirmSendChatMediaTitle,
        confirmText: l10n.publish.confirmSendChatMediaDescription,
        okText: l10n.publish.confirmYes,
        cancelText: l10n.publish.confirmCancel,
        screenKey,
      });
      return Boolean(response);
    } catch (error) {
      appLogger.error(
        'Failed to show send-media confirm sheet',
        'useAddContentSubmitSeams.showSendMediaConfirm',
        { error: error as Error },
      );
      return false;
    }
  }, [l10n, screenKey]);

  // Chat-flow submit seam: the Preview step's "Done" runs the chat-media upload
  // and closes the sheet via `onFinish` (after a Confirm sheet, no uploading
  // banner). `chat.onSendMedia` is `undefined` exactly when the chat flow is
  // absent (the sub-object pairs it with `chatUsersInfo`), so the hook's
  // "missing required data" branch covers the same defensive case.
  const { submit: handleChatSubmit } = useSendMessageMedia({
    selectedFile,
    mediaType,
    videoMeta,
    onFinish,
    onSendMedia: chat?.onSendMedia,
    onConfirm: showSendMediaConfirm,
    // The sheet owns the page-level upload-error toast; the hook only reports
    // the failure (the per-tile `error.png` in the chat list is the primary
    // signal). Fires globally even though the sheet has closed by then.
    onError: () => toast.error(l10n.publish.chatUploadError),
    logSource: 'useSendMessageMedia.submit(chat)',
  });

  // Community-flow submit seam: same hook, but hands the collected media
  // (image or video) to the community send path via `community.onSendMedia`,
  // which routes by `mediaType` (`useCommunity.sendImage` / `sendVideo`) and
  // toasts `community.uploadError` on failure.
  const { submit: handleCommunitySubmit } = useSendMessageMedia({
    selectedFile,
    mediaType,
    videoMeta,
    onFinish,
    onSendMedia: community?.onSendMedia,
    onConfirm: showSendMediaConfirm,
    onError: () => toast.error(l10n.community.uploadError),
    logSource: 'useSendMessageMedia.submit(community)',
  });

  // Feedback-flow submit seam: reuses the same collector hook, but the Preview
  // "Done" COLLECTS rather than sends — it hands the picked `{ file, aspectRatio }`
  // to `feedback.onPickImage` (which `FeedbackPage` appends to its draft list)
  // and closes the sheet. Deliberately NO `onConfirm` (Flutter :176 just appends
  // — no confirm sheet) and NO `onError` (the adapter always resolves `true`, so
  // there is no failure path / toast). Feedback is image-only, so only the image
  // fields of `SendMessageMediaArgs` are forwarded.
  const { submit: handleFeedbackSubmit } = useSendMessageMedia({
    selectedFile,
    mediaType,
    videoMeta,
    onFinish,
    onSendMedia: feedback
      ? async (args) => {
          feedback.onPickImage({ file: args.file, aspectRatio: args.aspectRatio });
          return true;
        }
      : undefined,
    onConfirm: undefined,
    logSource: 'useSendMessageMedia.submit(feedback)',
  });

  // Profile-image submit seam: a direct sibling of the feedback one — the
  // Preview "Done" COLLECTS the picked `{ file, aspectRatio }` and hands it to
  // `profileImage.onPickImage` (which `EditUserProfilePage` turns into a local
  // object-URL avatar preview), then closes the sheet. Deliberately NO
  // `onConfirm` and NO `onError`: Flutter's picker call
  // (`edit_user_profile_screen.dart:272-279`) opens the picker with
  // `returnLocalMedia: true, uploadOnly: true` and simply assigns the result
  // (`:280-285`) — no upload, no confirm dialog, no failure toast at pick time.
  // The adapter always resolves `true`, so there is no failure path to report.
  // The real upload runs later, from the page's own "Done" CTA.
  const { submit: handleProfileImageSubmit } = useSendMessageMedia({
    selectedFile,
    mediaType,
    videoMeta,
    onFinish,
    onSendMedia: profileImage
      ? async (args) => {
          profileImage.onPickImage({ file: args.file, aspectRatio: args.aspectRatio });
          return true;
        }
      : undefined,
    onConfirm: undefined,
    logSource: 'useSendMessageMedia.submit(profileImage)',
  });

  // The Preview "Done" seam routes to a per-flow submit handler by the single
  // `flowType` discriminator. `chat` is the `default` so any non-feedback /
  // non-community / non-profileImage message flow keeps the chat send behavior
  // (matches the prior chained-ternary fallback).
  const handleMessageSubmit = useMemo(() => {
    switch (flowType) {
      case AddContentFlowType.feedback:
        return handleFeedbackSubmit;
      case AddContentFlowType.community:
        return handleCommunitySubmit;
      case AddContentFlowType.profileImage:
        return handleProfileImageSubmit;
      case AddContentFlowType.chat:
      default:
        return handleChatSubmit;
    }
  }, [
    flowType,
    handleFeedbackSubmit,
    handleCommunitySubmit,
    handleProfileImageSubmit,
    handleChatSubmit,
  ]);

  return { handleMessageSubmit };
};
